import React, { useCallback, useMemo, useRef } from 'react';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { Helmet } from 'react-helmet';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { SectionGradient } from 'apps/sigma2-ui/src/pages/FocusAreaPageComponent';
import { OptimizingCard } from 'apps/sigma2-ui/src/components/key-factors/optimizing-card/OptimizingCard';
import { RadialImpactChart } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/RadialImpactChart';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { SuccessPhenotypeV2CustomVizDefinition } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeDefinition';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { SuccessPhenotypeV2Request } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeTypes';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { buildSuccessPhenotypeViz } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { getSuccessPhenotypeFiltersFromCustomViz } from 'apps/sigma2-ui/src/filters-v2/components/getCustomVizFromFilters';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { Trait } from 'libs/end-to-end/src/success-phenotype';
import { SuccessPhenotypeV2Result } from 'libs/end-to-end/src/success-phenotype';
import { getKeyFactorsDataQualityScoreLevel } from 'libs/end-to-end/src/success-phenotype';
import { determineSuccessPhenotypeV2ErrorStateReason } from 'libs/end-to-end/src/success-phenotype';
import { traitsToBubbleGraphDatums } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { traitToTraitLevelDatumsForKeyFactors } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { BreakdownSection } from 'apps/sigma2-ui/src/components/key-factors/BreakdownSection';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { useIndexedHover } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/utils';
import { isEntitledToSuccessFactors } from 'libs/end-to-end/src/group-entitlements';
import { isKeyFactorsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';

export const KeyFactorsPage: React.FC = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const { myObservation } = useObservation();
  const language = useLanguage();
  useAuthenticatedPageRedirect();

  const orgId = useMemo(
    () => myUser?.organization?.id,
    [myUser?.organization?.id],
  );

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const keyFactorsAvailable = useMemo(() => {
    return (
      isKeyFactorsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToSuccessFactors({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  return (
    <MainLayout showBackToTop={true}>
      <Helmet>
        <title>{documentTitle(language.keyFactorsPageTitle())}</title>
      </Helmet>

      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isLoadingStill}
        failedToLoadObservation={failedToLoadObservation}
      />

      {!isLoadingStill && myObservation && keyFactorsAvailable && orgId && (
        <>
          <SectionGradient>
            <PageSection>
              <div className="mt-0">
                <S2Typography
                  variant="headline-3"
                  translatedContent={language.keyFactorsPageTitle()}
                />
                {myObservation &&
                  myObservation.translationPhrasingCode !== 'en-pd' && (
                    <S2Typography
                      variant="body-6"
                      textColorVariant="gray-800"
                      translatedContent={language.keyFactorsPageSubHeading()}
                    />
                  )}
              </div>
            </PageSection>

            <PageSection>
              <CustomViz
                observation={myObservation.type}
                organizationId={orgId}
                customVizDefinition={SuccessPhenotypeV2CustomVizDefinition}
                userAuthenticatedApiCall={userAuthenticatedApiCall}
              >
                <KeyFactorsContent
                  organizationId={orgId}
                  myObservation={myObservation}
                />
              </CustomViz>
            </PageSection>
          </SectionGradient>
        </>
      )}
    </MainLayout>
  );
};

const KeyFactorsContent: React.FC<{
  organizationId: string;
  myObservation: MyObservation;
}> = ({ organizationId, myObservation }) => {
  const context = useCustomViz();

  const { doViz } = context;

  const shareLinkRawData = useShareLinkSearchParameter();

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): { customVizRequest: SuccessPhenotypeV2Request | undefined } => {
      const customVizRequest =
        buildSuccessPhenotypeViz({
          primaryFilterSelections,
          secondaryFilterSelections,
          myObservation,
          organizationId,
        }) ?? undefined;

      return {
        customVizRequest,
      };
    },
    [myObservation, organizationId],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest) {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest],
  );

  const onQueryProgress = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const downloadContainerRef = useRef<HTMLDivElement>(null);

  const getElementToDownload = useCallback(
    // this callback is only used if downloadContainerRef is not null so we
    // can safely assert it is not null at this point
    () => downloadContainerRef!.current,
    [downloadContainerRef],
  );

  const filterPayload = useFilters({
    feature: 'successFactors',
    focusArea: 'Performance',
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onQueryProgress,
    getCustomVizRequest,
    getElementToDownload,
    analysisName: undefined,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (
        !myObservation ||
        !request ||
        request.customVizId !== 'successPhenotypeV2'
      ) {
        return;
      }

      const { primaryFilterSelections, secondaryFilterSelections } =
        getSuccessPhenotypeFiltersFromCustomViz({
          customVizRequest: request,
          myObservation,
        });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation, onAnalyzeWithParams],
  );

  const { userAuthenticatedApiCall, myUser } = useUser();

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: SuccessPhenotypeV2Request,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <Spacing ref={downloadContainerRef} direction="vertical" size="md">
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

      <HorizontalFilterBar
        items={filterPayload.items}
        slotRight={
          <DataRefreshIndicator
            status={context.status}
            resultCreatedAt={context.resultCreatedAt}
          />
        }
      />

      {lastCustomVizRequest && (
        <div className="h-full min-h-96 @container">
          <KeyFactorsCard
            customVizRequest={lastCustomVizRequest}
            result={context.result}
            status={context.status}
          />
        </div>
      )}
    </Spacing>
  );
};

export const KeyFactorsCard: React.FC<{
  customVizRequest: SuccessPhenotypeV2Request;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  // eslint-disable-next-line complexity
}> = ({ customVizRequest, result, status }) => {
  const { myObservation } = useObservation();

  const [selectedTrait, setSelectedTrait] = React.useState<Trait | null>(null);

  React.useEffect(() => {
    setSelectedTrait(null);
  }, [result]);

  const { readyToDisplayDynamicContent, isLoading, data, hasFailed } =
    useMemo(() => {
      return parseCustomVizResult({
        result: result,
        status: status,
        ResultZodType: SuccessPhenotypeV2Result,
      });
    }, [result, status]);

  const impactDataPoints = useMemo(() => {
    if (!data || !myObservation) {
      return [];
    }

    const controlVariableDefinitionsMapping =
      getSuccessPhenotypeV2ControlVariableDefinitionsMappingFromObservation({
        myObservation,
      });

    const datums = traitsToBubbleGraphDatums(data.traits);

    return datums.map((datum) => {
      const { datums: traitDatum, bestTraitDatumIndex } =
        traitToTraitLevelDatumsForKeyFactors({
          controlVariableDefinitionsMapping,
          activeTrait: datum.trait,
        });

      const controlVariableDefinition =
        controlVariableDefinitionsMapping[
          datum.trait.controlVariableColumnHeaderValue
        ];

      return {
        impact: datum.bestTraitLevel.percentage,
        keyFactorImpactValue:
          bestTraitDatumIndex === null
            ? ''
            : traitDatum[bestTraitDatumIndex].id,
        keyFactorImpactLabel: controlVariableDefinition.displayName,
        sampleSize: datum.trait.sampleSize,
        controlVariableColumnHeaderValue:
          datum.trait.controlVariableColumnHeaderValue,
      };
    });
  }, [data, myObservation]);

  const handleSelectTrait = useCallback(
    (controlVariableColumnHeaderValue: string) => {
      const trait = data?.traits.find(
        (point) =>
          point.controlVariableColumnHeaderValue ===
          controlVariableColumnHeaderValue,
      );

      if (trait) {
        setSelectedTrait(trait);
      }
    },
    [data?.traits],
  );

  const metricColumnHeaderValue = useMemo(() => {
    return customVizRequest.inputs.extras.metric;
  }, [customVizRequest]);

  const [isTraitHovered, setIsTraitHovered] = useIndexedHover(
    impactDataPoints.length,
  );

  const traits = useMemo(() => data?.traits ?? [], [data?.traits]);
  const effectiveErrorStateReason = useMemo(() => {
    return determineSuccessPhenotypeV2ErrorStateReason({
      traits,
      errorStateReason: data?.errorStateReason,
      isVizFailure: hasFailed,
    });
  }, [data?.errorStateReason, traits, hasFailed]);

  const dataQualityScoreLevel = useMemo(() => {
    return getKeyFactorsDataQualityScoreLevel(data);
  }, [data]);

  const numberOfPeople = useMemo(() => {
    return data?.fullSampleSize ?? 0;
  }, [data]);

  return (
    <div className="w-full justify-center flex h-full">
      {isLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          h="full"
          w="full"
          flex="1"
        >
          <CustomVizCircularSpinner />
        </Box>
      )}

      {selectedTrait && !isLoading && readyToDisplayDynamicContent && (
        <BreakdownSection
          controlVariableColumnHeaderValue={
            selectedTrait.controlVariableColumnHeaderValue
          }
          metricColumnHeaderValue={metricColumnHeaderValue}
          trait={selectedTrait}
          traits={data?.traits ?? []}
          setSelectedTrait={setSelectedTrait}
          explanations={data.explanations}
        />
      )}

      {!isLoading &&
        !selectedTrait &&
        (readyToDisplayDynamicContent || hasFailed) && (
          <div
            data-testid="key-factors-card"
            className=" flex flex-col-reverse @5xl:flex-row @5xl:items-start  w-full justify-between overflow-y-auto"
          >
            <OptimizingCard
              metricColumnHeaderValue={metricColumnHeaderValue}
              dataPoints={impactDataPoints}
              handleSelectTrait={handleSelectTrait}
              setIsTraitHovered={setIsTraitHovered}
              traits={data?.traits ?? []}
              explanations={data?.explanations}
              errorStateReason={effectiveErrorStateReason}
            />
            <RadialImpactChart
              dataPoints={impactDataPoints}
              handleSelectTrait={handleSelectTrait}
              isTraitHovered={isTraitHovered}
              setIsTraitHovered={setIsTraitHovered}
              dataQualityScoreLevel={dataQualityScoreLevel}
              numberOfPeople={numberOfPeople}
            />
          </div>
        )}
    </div>
  );
};
